import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../store/store';
import { logout } from '../store/slices/authSlice';
import { endSession, revokeLapsedRefreshToken } from '../utils/endSession';
import {
  SESSION_TIMEOUT_REDIRECT,
  isSessionIdleExpired,
  markSessionActivity,
  takeStaleRefreshToken,
} from '../utils/session';

// What counts as the user still being there. Deliberately not mousemove: a nudged desk should not
// keep a session alive for hours.
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];

// Activity is written to shared storage, so it is throttled rather than written on every event.
const ACTIVITY_WRITE_INTERVAL_MS = 15_000;
const IDLE_CHECK_INTERVAL_MS = 15_000;

/**
 * Signs the user out after a period of no activity, in every tab at once.
 *
 * The clock lives in localStorage, which all tabs of a browser share: working in one tab keeps the
 * others alive, and when it runs out they all end together. Ending it revokes the refresh token, so
 * the session is over on the server too and reopening the app asks for credentials again.
 */
export const useIdleSessionTimeout = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const endingRef = useRef(false);

  // A session that had already lapsed before this tab loaded still has a live refresh token on the
  // server until someone says otherwise.
  useEffect(() => {
    const lapsedRefreshToken = takeStaleRefreshToken();
    if (lapsedRefreshToken) {
      void revokeLapsedRefreshToken(lapsedRefreshToken);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    endingRef.current = false;
    let lastWrite = 0;

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_WRITE_INTERVAL_MS) return;
      lastWrite = now;
      markSessionActivity();
    };

    const timeOutSession = async () => {
      if (endingRef.current) return;
      endingRef.current = true;

      await endSession();
      dispatch(logout());
      navigate(SESSION_TIMEOUT_REDIRECT, { replace: true });
    };

    const checkIdle = () => {
      if (isSessionIdleExpired()) void timeOutSession();
    };

    // Coming back to the tab is the moment a lapsed session is most likely to be noticed, so check
    // then as well as on the interval.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkIdle();
    };

    // Signing out in one tab (or timing out there) must not leave this one sitting on a dashboard.
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'token' && !event.newValue) {
        dispatch(logout());
        navigate('/login', { replace: true });
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, handleActivity, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);
    const intervalId = window.setInterval(checkIdle, IDLE_CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated, dispatch, navigate]);
};
